import React, { useEffect, useState, Component } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { useAuth } from '../components/auth/AuthProvider';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, XIcon, InfoIcon, AlertCircleIcon, CheckIcon } from 'lucide-react';
import { createProduct, updateProduct, getProductById } from '../firebase/firestore';
import { FileUpload } from '../components/FileUpload';
import { uploadFile, generateStoragePath } from '../firebase/storage';
import { MultiImageUpload } from '../components/MultiImageUpload';
import { CustomCategoryDropdown } from '../components/CustomCategoryDropdown';
import { CustomFormatDropdown } from '../components/CustomFormatDropdown';
import { CustomIndustryDropdown } from '../components/CustomIndustryDropdown';
import { CustomPriceRangeDropdown } from '../components/CustomPriceRangeDropdown';
import { CustomUseCaseDropdown } from '../components/CustomUseCaseDropdown';
import { CustomPlatformDropdown } from '../components/CustomPlatformDropdown';
import { CustomSkillLevelDropdown } from '../components/CustomSkillLevelDropdown';
import { CustomCreatorTypeDropdown } from '../components/CustomCreatorTypeDropdown';
import { CustomLicenseTypeDropdown } from '../components/CustomLicenseTypeDropdown';
import { VideoUpload } from '../components/VideoUpload';
import { VideoTypeSelector } from '../components/VideoTypeSelector';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
interface FormData {
  title: string;
  description: string;
  shortDescription: string;
  price: string;
  category: string;
  platform: string;
  format: string;
  skillLevel: string;
  industry: string;
  creatorType: string;
  priceRange: string;
  licenseType: string;
  useCase: string;
  tags: string[];
  demoUrl: string;
  acceptTerms: boolean;
  // Video-specific fields
  videoUrl?: string;
  videoType?: string;
}
export const SellProductPage = () => {
  const navigate = useNavigate();
  const {
    isAuthenticated,
    user
  } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [productFile, setProductFile] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [productId, setProductId] = useState<string | null>(null);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [uploadingProductFile, setUploadingProductFile] = useState(false);
  const [uploadingPreviewImages, setUploadingPreviewImages] = useState(false);
  const [loadingPreferences, setLoadingPreferences] = useState(true);
  const [formData, setFormData] = useState<FormData>({
    title: '',
    description: '',
    shortDescription: '',
    price: '',
    category: '',
    platform: '',
    format: '',
    skillLevel: '',
    industry: '',
    creatorType: '',
    priceRange: '',
    licenseType: '',
    useCase: '',
    tags: [],
    demoUrl: '',
    acceptTerms: false,
    // Video-specific fields
    videoUrl: '',
    videoType: ''
  });
  // Ensure categories are exactly the same as in other parts of the application
  const categories = ['Website Templates', 'Mobile App Kits', 'UI Kits', 'AI Prompt Packs', 'Component Libraries', 'Design Systems', 'Animations', 'Audio Packs', 'No-Code MVPs', 'Notion Templates', 'eBooks & PDF Guides', 'Course Slides & Templates', 'Printable Planners', 'Email Templates & Flows', 'Copywriting Packs', 'Fonts & Typefaces', 'Icon Packs', '3D Assets & Mockups', 'Canva Templates', 'Social Media Content Packs', 'Legal Templates', 'Resume & Portfolio Templates', 'Short-form Video', 'Landscape Video', 'Other'];
  // Platform options
  const platforms = ['React', 'Angular', 'Vue', 'Svelte', 'Next.js', 'Gatsby', 'WordPress', 'Shopify', 'Webflow', 'HTML/CSS/JS', 'Figma', 'Sketch', 'Adobe XD', 'Adobe Photoshop', 'Adobe Illustrator', 'Canva', 'iOS', 'Android', 'Flutter', 'React Native', 'Unity', 'Unreal Engine', 'Other'];
  // Format options
  const formats = ['PSD', 'Sketch', 'Figma', 'XD', 'HTML/CSS', 'JavaScript', 'React Components', 'WordPress Theme', 'Shopify Theme', 'Mobile App Template', 'Illustrations', 'Icons', 'Fonts', '3D Models', 'Video Templates', 'Audio Files', 'PDF', 'Notion Template', 'Spreadsheet', 'Presentation', 'Other'];
  // Skill level options
  const skillLevels = ['Beginner', 'Intermediate', 'Advanced', 'All Levels'];
  // Industry options
  const industries = ['SaaS', 'E-commerce', 'Healthcare', 'Education', 'Finance', 'Real Estate', 'Travel', 'Food & Restaurant', 'Fitness', 'Beauty', 'Fashion', 'Technology', 'Creative', 'Marketing', 'Entertainment', 'Non-profit', 'Other'];
  // Creator type options
  const creatorTypes = ['Designer', 'Developer', 'Marketer', 'Entrepreneur', 'Educator', 'Writer', 'Photographer', 'Videographer', 'Illustrator', '3D Artist', 'UI/UX Designer', 'Product Designer', 'Content Creator', 'Agency', 'Other'];
  // Price range options
  const priceRanges = ['Free', 'Under $10', '$10 - $25', '$25 - $50', '$50 - $100', '$100 - $200', '$200+'];
  // License type options
  const licenseTypes = ['Standard License', 'Extended License', 'Commercial License', 'Personal Use Only', 'MIT License', 'GPL License', 'Creative Commons', 'Custom License'];
  // Use case options
  const useCases = ['Personal Website', 'Business Website', 'E-commerce Store', 'Blog', 'Portfolio', 'Landing Page', 'Admin Dashboard', 'Mobile App', 'Web App', 'Marketing Campaign', 'Social Media', 'Email Marketing', 'Presentation', 'Documentation', 'Learning Materials', 'Other'];
  // Add state for video type
  const [videoType, setVideoType] = useState<string>('motivational');
  // Fetch user preferences
  useEffect(() => {
    const fetchUserPreferences = async () => {
      if (!user?.id) return;
      try {
        setLoadingPreferences(true);
        const preferencesDocRef = doc(db, 'userPreferences', user.id);
        const preferencesDoc = await getDoc(preferencesDocRef);
        if (preferencesDoc.exists()) {
          const data = preferencesDoc.data();
          const productPreferences = data.productPreferences || {};
          // Update form data with preferences
          setFormData(prev => ({
            ...prev,
            category: productPreferences.defaultCategory || prev.category,
            price: productPreferences.defaultPrice || prev.price,
            demoUrl: productPreferences.defaultExternalLink || prev.demoUrl
          }));
        }
      } catch (error) {
        console.error('Error fetching user preferences:', error);
      } finally {
        setLoadingPreferences(false);
      }
    };
    fetchUserPreferences();
  }, [user]);
  // Scroll handler for navbar
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);
  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/sell-with-us', {
        state: {
          openAuth: true,
          returnTo: '/sell-product'
        }
      });
    }
  }, [isAuthenticated, navigate]);
  // Check if we're editing an existing product
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const editProductId = queryParams.get('edit');
    if (editProductId) {
      setIsEditing(true);
      setProductId(editProductId);
      // Fetch the product data
      const fetchProduct = async () => {
        try {
          const product = await getProductById(editProductId);
          if (product) {
            setFormData({
              title: product.title || '',
              description: product.description || '',
              shortDescription: product.shortDescription || '',
              price: product.price.toString() || '',
              category: product.category || '',
              platform: product.platform || '',
              format: product.format || '',
              skillLevel: product.skillLevel || '',
              industry: product.industry || '',
              creatorType: product.creatorType || '',
              priceRange: product.priceRange || '',
              licenseType: product.licenseType || '',
              useCase: product.useCase || '',
              tags: product.tags || [],
              demoUrl: product.demoUrl || '',
              image: product.image || '',
              previewImages: product.previewImages || [],
              productFileUrl: product.fileUrl || '',
              sellerName: product.sellerName || '',
              sellerId: product.sellerId || '',
              acceptTerms: true
            });
            if (product.image) {
              setThumbnailUrl(product.image);
            }
            if (product.previewImages) {
              setPreviewImages(product.previewImages);
            }
            if (product.fileUrl) {
              setProductFile(product.fileUrl);
            }
          }
        } catch (error) {
          console.error('Error fetching product:', error);
        }
      };
      fetchProduct();
    }
  }, [isAuthenticated, navigate, location.search]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
    // Clear error when field is being edited
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = {
          ...prev
        };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  const handleTagInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentTag(e.target.value);
  };
  const handleAddTag = () => {
    if (currentTag.trim() && !formData.tags.includes(currentTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, currentTag.trim()]
      }));
      setCurrentTag('');
    }
  };
  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };
  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };
  const handleThumbnailUpload = (url: string) => {
    setThumbnailUrl(url);
    setUploadingThumbnail(false);
    // Clear error
    if (errors.thumbnail) {
      setErrors(prev => {
        const newErrors = {
          ...prev
        };
        delete newErrors.thumbnail;
        return newErrors;
      });
    }
  };
  const handlePreviewImagesUpload = (urls: string[]) => {
    setPreviewImages(urls);
    setUploadingPreviewImages(false);
    // Clear error
    if (errors.previewImages) {
      setErrors(prev => {
        const newErrors = {
          ...prev
        };
        delete newErrors.previewImages;
        return newErrors;
      });
    }
  };
  const handleProductFileUpload = (url: string) => {
    setProductFile(url);
    setUploadingProductFile(false);
    // Clear error
    if (errors.productFile) {
      setErrors(prev => {
        const newErrors = {
          ...prev
        };
        delete newErrors.productFile;
        return newErrors;
      });
    }
  };
  // Handle video upload completion
  const handleVideoUploadComplete = (downloadUrl: string, metadata?: any) => {
    console.log('Video upload completed, URL:', downloadUrl.substring(0, 30) + '...', 'Metadata:', metadata);
    setFormData(prev => ({
      ...prev,
      videoUrl: downloadUrl,
      // Store video metadata if available
      videoMetadata: metadata || undefined
    }));
    // Clear error
    if (errors.videoUrl) {
      setErrors(prev => {
        const newErrors = {
          ...prev
        };
        delete newErrors.videoUrl;
        return newErrors;
      });
    }
  };
  // Handle video type selection
  const handleVideoTypeChange = (type: string) => {
    setVideoType(type);
    setFormData(prev => ({
      ...prev,
      videoType: type
    }));
  };
  const validateForm = () => {
    try {
      const newErrors: Record<string, string> = {};
      // Basic validation
      if (!formData.title || !formData.title.trim()) {
        newErrors.title = 'Product title is required';
      }
      if (!formData.description || !formData.description.trim()) {
        newErrors.description = 'Description is required';
      }
      if (!formData.price || !formData.price.trim()) {
        newErrors.price = 'Price is required';
      } else {
        const priceNum = Number(formData.price);
        if (isNaN(priceNum) || priceNum < 0) {
          newErrors.price = 'Please enter a valid price';
        }
      }
      if (!formData.category) {
        newErrors.category = 'Category is required';
      }
      if (!thumbnailUrl) {
        newErrors.thumbnail = 'Thumbnail image is required';
      }
      // Video-specific validation
      if (formData.category === 'Short-form Video' || formData.category === 'Landscape Video') {
        console.log('Validating video product:', {
          videoUrl: formData.videoUrl ? 'Present' : 'Missing',
          videoType: formData.videoType || 'Not set',
          videoMetadata: formData.videoMetadata,
          category: formData.category
        });
        if (!formData.videoUrl) {
          newErrors.videoUrl = `Video file is required for ${formData.category} products`;
        }
        // Validate dimensions based on video category
        if (formData.videoMetadata) {
          const {
            width,
            height
          } = formData.videoMetadata;
          if (formData.category === 'Short-form Video' && width >= height) {
            newErrors.videoUrl = `Short-form video must be vertical (portrait). Your video is ${width}×${height}. Recommended: 1080×1920 for best quality.`;
          }
          if (formData.category === 'Landscape Video' && (width !== 1920 || height !== 1080)) {
            newErrors.videoUrl = `Video must be exactly 1920×1080 pixels. Your video is ${width}×${height}.`;
          }
        }
      } else if (!productFile) {
        // Only require product file for non-video products
        newErrors.productFile = 'Product file is required';
      }
      if (!formData.acceptTerms) {
        newErrors.acceptTerms = 'You must accept the terms and conditions';
      }
      console.log('Validation complete, errors:', newErrors);
      return newErrors;
    } catch (error) {
      console.error('Error during form validation:', error);
      return {
        validation: 'An unexpected error occurred during validation'
      };
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submission started', e.type);
    try {
      // Debug current form state
      console.log('Current form state:', {
        title: formData.title,
        description: formData.description.substring(0, 30) + '...',
        price: formData.price,
        category: formData.category,
        videoUrl: formData.videoUrl ? 'Present (URL omitted for brevity)' : 'Missing',
        videoType: formData.videoType,
        thumbnailUrl: thumbnailUrl ? 'Present (URL omitted for brevity)' : 'Missing',
        acceptTerms: formData.acceptTerms
      });
      const newErrors = validateForm();
      console.log('Validation result:', {
        hasErrors: Object.keys(newErrors).length > 0,
        errors: newErrors
      });
      if (Object.keys(newErrors).length > 0) {
        console.log('Validation errors:', newErrors);
        setErrors(newErrors);
        // Scroll to the first error
        const firstErrorField = document.querySelector(`[name="${Object.keys(newErrors)[0]}"]`);
        if (firstErrorField) {
          firstErrorField.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
        return;
      }
      setFormStatus('submitting');
      console.log('Form status set to submitting');
      // Create product in Firestore
      const productData = {
        title: formData.title,
        description: formData.description,
        shortDescription: formData.shortDescription || formData.description.substring(0, 150),
        price: Number(formData.price),
        category: formData.category,
        platform: formData.platform,
        format: formData.format,
        skillLevel: formData.skillLevel,
        industry: formData.industry,
        creatorType: formData.creatorType,
        priceRange: formData.priceRange,
        licenseType: formData.licenseType,
        useCase: formData.useCase,
        tags: formData.tags,
        demoUrl: formData.demoUrl,
        image: thumbnailUrl,
        previewImages: previewImages,
        // Use videoUrl as the product file for video products
        productFileUrl: formData.category === 'Short-form Video' || formData.category === 'Landscape Video' ? formData.videoUrl : productFile,
        // Add video-specific fields if category is a video type
        ...((formData.category === 'Short-form Video' || formData.category === 'Landscape Video') && {
          videoUrl: formData.videoUrl,
          videoType: formData.videoType || 'motivational',
          isVideo: true,
          // Include video metadata for dimension validation
          videoMetadata: formData.videoMetadata || (formData.category === 'Short-form Video' ? {
            width: 1080,
            height: 1920,
            isVertical: true,
            aspectRatio: 9 / 16
          } : {
            width: 1920,
            height: 1080,
            isVertical: false,
            aspectRatio: 16 / 9
          })
        }),
        sellerName: user?.name || 'Unknown Seller',
        sellerId: user?.id || '',
        status: 'published'
      };
      console.log('Product data prepared, sending to Firestore...');
      const productId = await createProduct(productData);
      console.log('Product created successfully with ID:', productId);
      setFormStatus('success');
      // Redirect to seller dashboard after short delay
      setTimeout(() => {
        navigate('/seller-dashboard');
      }, 2000);
    } catch (error) {
      console.error('Error during form submission:', error);
      setFormStatus('error');
      // Show more specific error information
      setErrors({
        submission: error instanceof Error ? error.message : 'Unknown error occurred'
      });
    }
  };
  const [currentTag, setCurrentTag] = useState('');
  if (!isAuthenticated) {
    return null; // Don't render anything if not authenticated
  }
  if (formStatus === 'success') {
    return <div className="min-h-screen bg-[#F8FAFA] dark:bg-[#0F1F1D] overflow-x-hidden max-w-full transition-colors duration-300" data-id="element-6036">
        <Navbar scrolled={scrolled} data-id="element-6037" />
        <main className="container mx-auto px-4 py-24 md:py-32 max-w-full" data-id="element-6038">
          <div className="max-w-3xl mx-auto bg-white dark:bg-[#162825] p-8 rounded-2xl shadow-sm border border-gray-100 dark:border-[#1A3330] text-center" data-id="element-6039">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-6040">
              <CheckIcon className="h-8 w-8 text-green-600 dark:text-green-400" data-id="element-6041" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 dark:text-[#F8FAF9] mb-4" data-id="element-6042">
              Product Added Successfully!
            </h3>
            <p className="text-gray-600 dark:text-[#B0C1BD] mb-6" data-id="element-6043">
              Your product has been added to our marketplace. You can view it or
              make changes from your dashboard.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-6044">
              <button onClick={() => navigate('/seller-dashboard')} className="bg-gradient-to-r from-[#0A3C52] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white px-6 py-3 rounded-full text-base font-medium hover:shadow-lg transition-all" data-id="element-6045">
                Go to Dashboard
              </button>
              <button onClick={() => setFormStatus('idle')} className="bg-gray-100 dark:bg-[#1A3330] text-gray-800 dark:text-[#B0C1BD] px-6 py-3 rounded-full text-base font-medium hover:bg-gray-200 dark:hover:bg-[#0B2C2A] transition-all" data-id="element-6046">
                Add Another Product
              </button>
            </div>
          </div>
        </main>
        <Footer data-id="element-6047" />
      </div>;
  }
  return <div className="min-h-screen bg-[#F8FAFA] dark:bg-[#0F1F1D] overflow-x-hidden max-w-full transition-colors duration-300" data-id="element-6048">
      <Navbar scrolled={scrolled} data-id="element-6049" />
      <main className="container mx-auto px-4 py-24 md:py-32 max-w-full" data-id="element-6050">
        <div className="max-w-3xl mx-auto" data-id="element-6051">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 dark:text-[#F8FAF9] mb-6" data-id="element-6052">
            Add New Product
          </h1>
          <p className="text-gray-600 dark:text-[#B0C1BD] mb-10" data-id="element-6053">
            Fill out the form below to add your product to our marketplace. All
            fields marked with * are required.
          </p>
          {loadingPreferences && <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-900/30 rounded-lg p-4 mb-8" data-id="element-6054">
              <div className="flex items-center" data-id="element-6055">
                <InfoIcon className="h-5 w-5 text-blue-500 dark:text-blue-400 mr-3 flex-shrink-0" data-id="element-6056" />
                <p className="text-blue-700 dark:text-blue-300 text-sm" data-id="element-6057">
                  Loading your default preferences...
                </p>
              </div>
            </div>}
          {formStatus === 'error' && <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30 rounded-lg p-4 mb-8" data-id="element-6058">
              <div className="flex" data-id="element-6059">
                <AlertCircleIcon className="h-5 w-5 text-red-600 dark:text-red-400 mr-3 flex-shrink-0" data-id="element-6060" />
                <div data-id="element-6061">
                  <h3 className="text-sm font-medium text-red-800 dark:text-red-300" data-id="element-6062">
                    There was an error adding your product
                  </h3>
                  <p className="mt-1 text-sm text-red-700 dark:text-red-400" data-id="element-6063">
                    Please try again or contact support if the problem persists.
                  </p>
                </div>
              </div>
            </div>}
          <form onSubmit={handleSubmit} className="space-y-8" noValidate data-id="element-6064">
            {/* Basic Information */}
            <div className="bg-white dark:bg-[#162825] p-6 rounded-xl shadow-sm border border-gray-100 dark:border-[#1A3330]" data-id="element-6065">
              <h2 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-6" data-id="element-6066">
                Basic Information
              </h2>
              <div className="space-y-6" data-id="element-6067">
                <div data-id="element-6068">
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-6069">
                    Product Title *
                  </label>
                  <input type="text" id="title" name="title" value={formData.title} onChange={handleChange} className={`w-full px-4 py-2 rounded-lg border ${errors.title ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-[#1A3330]'} focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent dark:bg-[#0F1F1D] dark:text-[#B0C1BD]`} placeholder="E.g. Premium Website Template" data-id="element-6070" />
                  {errors.title && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-6071">
                      {errors.title}
                    </p>}
                </div>
                <div data-id="element-6072">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-6073">
                    Full Description *
                  </label>
                  <textarea id="description" name="description" value={formData.description} onChange={handleChange} rows={5} className={`w-full px-4 py-2 rounded-lg border ${errors.description ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-[#1A3330]'} focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent dark:bg-[#0F1F1D] dark:text-[#B0C1BD]`} placeholder="Describe your product in detail" data-id="element-6074" />
                  {errors.description && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-6075">
                      {errors.description}
                    </p>}
                </div>
                <div data-id="element-6076">
                  <label htmlFor="shortDescription" className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-6077">
                    Short Description (Optional)
                  </label>
                  <textarea id="shortDescription" name="shortDescription" value={formData.shortDescription} onChange={handleChange} rows={2} className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-[#1A3330] focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent dark:bg-[#0F1F1D] dark:text-[#B0C1BD]" placeholder="A brief summary of your product (150 characters max)" maxLength={150} data-id="element-6078" />
                  <p className="mt-1 text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-6079">
                    This will be shown in product cards and search results. If
                    left empty, we'll use the first part of your full
                    description.
                  </p>
                </div>
                <div data-id="element-6080">
                  <label htmlFor="price" className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-6081">
                    Price (USD) *
                  </label>
                  <div className="relative max-w-xs" data-id="element-6082">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 dark:text-[#7A8F8A]" data-id="element-6083">
                      $
                    </span>
                    <input type="text" id="price" name="price" value={formData.price} onChange={handleChange} className={`w-full pl-8 pr-4 py-2 rounded-lg border ${errors.price ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-[#1A3330]'} focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent dark:bg-[#0F1F1D] dark:text-[#B0C1BD]`} placeholder="29.99" data-id="element-6084" />
                  </div>
                  {errors.price && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-6085">
                      {errors.price}
                    </p>}
                </div>
              </div>
            </div>
            {/* Categorization */}
            <div className="bg-white dark:bg-[#162825] p-6 rounded-xl shadow-sm border border-gray-100 dark:border-[#1A3330]" data-id="element-6086">
              <h2 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-6" data-id="element-6087">
                Categorization
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-6088">
                <div data-id="element-6089">
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-6090">
                    Category *
                  </label>
                  <CustomCategoryDropdown id="category" name="category" value={formData.category} onChange={handleChange} options={categories} error={!!errors.category} data-id="element-6091" />
                  {errors.category && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-6092">
                      {errors.category}
                    </p>}
                </div>
                <div data-id="element-6093">
                  <label htmlFor="platform" className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-6094">
                    Platform (Optional)
                  </label>
                  <CustomPlatformDropdown id="platform" name="platform" value={formData.platform} onChange={handleChange} options={platforms} error={false} data-id="element-6095" />
                </div>
                <div data-id="element-6096">
                  <label htmlFor="format" className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-6097">
                    Format (Optional)
                  </label>
                  <CustomFormatDropdown id="format" name="format" value={formData.format} onChange={handleChange} options={formats} error={false} data-id="element-6098" />
                </div>
                <div data-id="element-6099">
                  <label htmlFor="skillLevel" className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-6100">
                    Skill Level (Optional)
                  </label>
                  <CustomSkillLevelDropdown id="skillLevel" name="skillLevel" value={formData.skillLevel} onChange={handleChange} options={skillLevels} error={false} data-id="element-6101" />
                </div>
                <div data-id="element-6102">
                  <label htmlFor="industry" className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-6103">
                    Industry / Niche (Optional)
                  </label>
                  <CustomIndustryDropdown id="industry" name="industry" value={formData.industry} onChange={handleChange} options={industries} error={false} data-id="element-6104" />
                </div>
                <div data-id="element-6105">
                  <label htmlFor="creatorType" className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-6106">
                    Creator Type (Optional)
                  </label>
                  <CustomCreatorTypeDropdown id="creatorType" name="creatorType" value={formData.creatorType} onChange={handleChange} options={creatorTypes} error={false} data-id="element-6107" />
                </div>
                <div data-id="element-6108">
                  <label htmlFor="priceRange" className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-6109">
                    Price Range (Optional)
                  </label>
                  <CustomPriceRangeDropdown id="priceRange" name="priceRange" value={formData.priceRange} onChange={handleChange} options={priceRanges} error={false} data-id="element-6110" />
                </div>
                <div data-id="element-6111">
                  <label htmlFor="licenseType" className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-6112">
                    License Type (Optional)
                  </label>
                  <CustomLicenseTypeDropdown id="licenseType" name="licenseType" value={formData.licenseType} onChange={handleChange} options={licenseTypes} error={false} data-id="element-6113" />
                </div>
                <div data-id="element-6114">
                  <label htmlFor="useCase" className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-6115">
                    Use Case (Optional)
                  </label>
                  <CustomUseCaseDropdown id="useCase" name="useCase" value={formData.useCase} onChange={handleChange} options={useCases} error={false} data-id="element-6116" />
                </div>
              </div>
              <div className="mt-6" data-id="element-6117">
                <label htmlFor="tags" className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-6118">
                  Tags (Optional)
                </label>
                <div className="flex" data-id="element-6119">
                  <input type="text" id="tags" value={currentTag} onChange={handleTagInputChange} onKeyDown={handleTagKeyDown} className="flex-grow px-4 py-2 rounded-l-lg border border-gray-300 dark:border-[#1A3330] focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent dark:bg-[#0F1F1D] dark:text-[#B0C1BD]" placeholder="Add a tag and press Enter" data-id="element-6120" />
                  <button type="button" onClick={handleAddTag} className="px-4 py-2 bg-gray-100 dark:bg-[#1A3330] text-gray-700 dark:text-[#B0C1BD] rounded-r-lg border border-l-0 border-gray-300 dark:border-[#1A3330] hover:bg-gray-200 dark:hover:bg-[#0B2C2A]" data-id="element-6121">
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 mt-3" data-id="element-6122">
                  {formData.tags.map(tag => <div key={tag} className="flex items-center bg-gray-100 dark:bg-[#1A3330] text-gray-800 dark:text-[#B0C1BD] px-3 py-1 rounded-full text-sm" data-id="element-6123">
                      <span data-id="element-6124">{tag}</span>
                      <button type="button" onClick={() => handleRemoveTag(tag)} className="ml-2 text-gray-500 dark:text-[#7A8F8A] hover:text-gray-700 dark:hover:text-[#B0C1BD]" data-id="element-6125">
                        <XIcon className="h-3 w-3" data-id="element-6126" />
                      </button>
                    </div>)}
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-6127">
                  Tags help buyers find your product. Add up to 10 relevant
                  tags.
                </p>
              </div>
            </div>
            {/* Media */}
            <div className="bg-white dark:bg-[#162825] p-6 rounded-xl shadow-sm border border-gray-100 dark:border-[#1A3330]" data-id="element-6128">
              <h2 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-6" data-id="element-6129">
                Media
              </h2>
              <div className="space-y-8" data-id="element-6130">
                <div data-id="element-6131">
                  <label className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-3" data-id="element-6132">
                    Thumbnail Image *
                  </label>
                  {user && <FileUpload userId={user.id} fileType="thumbnail" onUploadComplete={handleThumbnailUpload} accept="image/*" maxSizeMB={5} buttonText="Upload Thumbnail (1200 × 800px recommended)" showPreview={true} previewUrl={thumbnailUrl} maxWidth={1200} maxHeight={800} data-id="element-6133" />}
                  {errors.thumbnail && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-6134">
                      {errors.thumbnail}
                    </p>}
                </div>
                <div data-id="element-6135">
                  <label className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-3" data-id="element-6136">
                    Preview Images (Optional)
                  </label>
                  {user && <MultiImageUpload userId={user.id} onUploadsComplete={handlePreviewImagesUpload} maxImages={5} initialImages={previewImages} maxWidth={1200} maxHeight={800} data-id="element-6137" />}
                </div>
                {/* Conditional rendering based on category */}
                {formData.category === 'Short-form Video' || formData.category === 'Landscape Video' ? <>
                    {/* Video Upload Section */}
                    <div data-id="element-6138">
                      <label className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-3" data-id="element-6139">
                        Video File *
                      </label>
                      {user && <VideoUpload userId={user.id} onUploadComplete={handleVideoUploadComplete} accept="video/*" maxSizeMB={100} buttonText={formData.category === 'Short-form Video' ? 'Upload short-form video (1080×1920)' : 'Upload video (1920×1080)'} showPreview={true} previewUrl={formData.videoUrl} maxDurationSeconds={formData.category === 'Short-form Video' ? 60 : 300} enforceVerticalFormat={formData.category === 'Short-form Video'} data-id="element-6140" />}
                      {errors.videoUrl && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-6141">
                          {errors.videoUrl}
                        </p>}
                    </div>
                    {/* Video Type Selector */}
                    <VideoTypeSelector value={videoType} onChange={handleVideoTypeChange} className="mt-4" data-id="element-6142" />
                  </> /* Product File Upload for non-video products */ : <div data-id="element-6143">
                    <label className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-3" data-id="element-6144">
                      Product File *
                    </label>
                    {user && <FileUpload userId={user.id} fileType="product" onUploadComplete={handleProductFileUpload} accept="*/*" maxSizeMB={100} buttonText="Upload Product File (100MB max)" showPreview={false} data-id="element-6145" />}
                    {errors.productFile && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-6146">
                        {errors.productFile}
                      </p>}
                  </div>}
              </div>
            </div>
            {/* Additional Information */}
            <div className="bg-white dark:bg-[#162825] p-6 rounded-xl shadow-sm border border-gray-100 dark:border-[#1A3330]" data-id="element-6147">
              <h2 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-6" data-id="element-6148">
                Additional Information
              </h2>
              <div className="space-y-6" data-id="element-6149">
                <div data-id="element-6150">
                  <label htmlFor="demoUrl" className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-6151">
                    Demo URL (Optional)
                  </label>
                  <input type="url" id="demoUrl" name="demoUrl" value={formData.demoUrl} onChange={handleChange} className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-[#1A3330] focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent dark:bg-[#0F1F1D] dark:text-[#B0C1BD]" placeholder="https://example.com/demo" data-id="element-6152" />
                  <p className="mt-1 text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-6153">
                    Provide a link where buyers can see your product in action
                  </p>
                </div>
              </div>
            </div>
            {/* Terms and Submission */}
            <div className="bg-white dark:bg-[#162825] p-6 rounded-xl shadow-sm border border-gray-100 dark:border-[#1A3330]" data-id="element-6154">
              <div className="flex items-start mb-6" data-id="element-6155">
                <div className="flex items-center h-5" data-id="element-6156">
                  <input id="acceptTerms" name="acceptTerms" type="checkbox" checked={formData.acceptTerms} onChange={handleChange} className="focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] h-4 w-4 text-[#2DB489] dark:text-[#4ED1A0] border-gray-300 dark:border-[#1A3330] rounded" data-id="element-6157" />
                </div>
                <div className="ml-3" data-id="element-6158">
                  <label htmlFor="acceptTerms" className={`text-sm ${errors.acceptTerms ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-[#C4D1CC]'}`} data-id="element-6159">
                    I confirm that this product follows all marketplace rules
                    and is my original work or I have the rights to sell it *
                  </label>
                  {errors.acceptTerms && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-6160">
                      {errors.acceptTerms}
                    </p>}
                </div>
              </div>
              {/* Display any submission errors */}
              {errors.submission && <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30 rounded-lg p-4 mb-6" data-id="element-6161">
                  <div className="flex" data-id="element-6162">
                    <AlertCircleIcon className="h-5 w-5 text-red-600 dark:text-red-400 mr-3 flex-shrink-0" data-id="element-6163" />
                    <div data-id="element-6164">
                      <h3 className="text-sm font-medium text-red-800 dark:text-red-300" data-id="element-6165">
                        Submission Error
                      </h3>
                      <p className="mt-1 text-sm text-red-700 dark:text-red-400" data-id="element-6166">
                        {errors.submission}
                      </p>
                    </div>
                  </div>
                </div>}
              <div className="bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-100 dark:border-yellow-900/30 rounded-lg p-4 mb-6" data-id="element-6167">
                <div className="flex" data-id="element-6168">
                  <InfoIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mr-3 flex-shrink-0" data-id="element-6169" />
                  <p className="text-sm text-yellow-700 dark:text-yellow-300" data-id="element-6170">
                    Your product will be reviewed by our team before being
                    published. This typically takes 24-48 hours.
                  </p>
                </div>
              </div>
              <button type="submit" disabled={formStatus === 'submitting'} onClick={e => {
              if (formStatus !== 'submitting') {
                // This is a backup in case the form onSubmit is not firing
                console.log('Submit button clicked directly');
                handleSubmit(e as any);
              }
            }} className={`w-full bg-gradient-to-r from-[#0A3C52] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white px-6 py-3 rounded-full text-base font-medium hover:shadow-lg transition-all ${formStatus === 'submitting' ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-6171">
                {formStatus === 'submitting' ? <span className="flex items-center justify-center" data-id="element-6172">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-6173">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-6174"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-6175"></path>
                    </svg>
                    Submitting...
                  </span> : 'Add Product'}
              </button>
            </div>
          </form>
        </div>
      </main>
      <Footer data-id="element-6176" />
    </div>;
};